import React, { useState } from 'react';

import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Typography from '@mui/material/Typography';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import Button from 'src/components/ui/Button';
import Loader from 'src/components/ui/Loader';
import TinyPreviewCard from 'src/components/ui/TinyPreviewCard/TinyPreviewCard';
import Icon from 'src/components/ui/Icon';

import { colours } from 'src/constants/colours';
import useUser from 'src/hooks/useUser';

import type { Medium, NewsCategory, UserFavoriteNewsPostsQuery } from 'src/types/__generated__/graphql';
import { useUserFavoriteNewsPostsQuery } from 'src/hooks/__generated__/queries';
import { isBottomDiv } from 'src/utils/isBottom';
import { styledComponent, styledTag } from 'src/utils/styled';

interface StyledCardProps {
    $selected: boolean;
}

const StyledCard = styledComponent(Card)`
    ${(props: StyledCardProps) =>
        props.$selected &&
        `
        background-color: ${colours.secondary.O12};
    `}
    :not(:first-of-type) {
        margin-top: 0.7rem;
    }
    :last-of-type {
        margin-bottom: 0.7rem;
    }
    box-shadow: 0rem 0.25rem 2rem -0.5rem ${colours.blackOpacity.O16};
`;

const StyledDialog = styledComponent(Dialog)`
    .MuiDialog-paper {
        position: relative;
        max-height: 32rem;
    }
`;

const StyledDialogTitle = styledComponent(DialogTitle)``;

const StyledDialogContent = styledComponent(DialogContent)`
    display: flex;
    flex-direction: column;
    padding: 0 1rem;
`;

const StyledDialogIcon = styledComponent(DialogContent)`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 5rem 0;
`;

const StyledDialogActions = styledComponent(DialogActions)`
    border-top: 0.0625rem solid ${colours.grey.G200};
    padding: 1rem;
`;

const ScrollContainer = styledTag('div')`
    overflow-y: auto;
`;

StyledDialog.defaultProps = {
    maxWidth: 'xs',
    fullWidth: true,
};

const defaultLimit = 10;

interface Props {
    onSelection: (selectedNewsPostId: string) => void;
    open: boolean;
    onClose: () => void;
    companyId?: string;
}

const SelectNewsPostModal: React.FC<Props> = ({ onSelection, open, onClose, companyId }) => {
    const { t } = useTranslation(['news', 'favorites', 'common']);
    const [allDataLoaded, setAllDataLoaded] = useState<boolean>(false);
    const currentUser = useUser();
    const [selectedNewsPostId, setSelectedNewsPostId] = useState<string | undefined>();

    const {
        data: newsPosts,
        loading,
        fetchMore,
    } = useUserFavoriteNewsPostsQuery({
        variables: {
            companyId,
            offset: 0,
            limit: defaultLimit,
        },
        skip: !open || !currentUser?.id,
        fetchPolicy: 'network-only',
    });

    const results = newsPosts?.userFavoriteNewsPosts?.results;
    const newsPostCount = newsPosts?.userFavoriteNewsPosts?.total;

    const handleClose = () => {
        setSelectedNewsPostId(undefined);
        onClose();
    };

    const handleScroll = async (ev: React.UIEvent<HTMLDivElement, UIEvent>) => {
        const bottom = isBottomDiv(ev.currentTarget);
        const actualMyContactsLength = newsPosts?.userFavoriteNewsPosts ? newsPostCount : 0;
        if (bottom && !allDataLoaded) {
            await fetchMore({
                variables: {
                    offset: actualMyContactsLength,
                },
                updateQuery(prev, { fetchMoreResult }: { fetchMoreResult?: UserFavoriteNewsPostsQuery }) {
                    if (!fetchMoreResult) return prev;
                    if (
                        fetchMoreResult.userFavoriteNewsPosts?.results &&
                        fetchMoreResult.userFavoriteNewsPosts.results.length < defaultLimit
                    ) {
                        setAllDataLoaded(true);
                    }
                    const prevItems = prev.userFavoriteNewsPosts?.results ?? [];
                    const newItems = fetchMoreResult.userFavoriteNewsPosts?.results ?? [];
                    return {
                        userFavoriteNewsPosts: {
                            total: fetchMoreResult.userFavoriteNewsPosts?.total ?? 0,
                            results: [...prevItems, ...newItems],
                        },
                    };
                },
            });
        }
    };

    return (
        <StyledDialog open={open} onClose={handleClose} fullWidth>
            <StyledDialogTitle onClose={handleClose}>
                <Box display="flex" flexDirection="row" alignItems="center">
                    <Typography variant="h3">{t('news:linkNewsPost')}</Typography>
                </Box>
            </StyledDialogTitle>
            {Number(newsPostCount) > 0 || loading ? (
                <StyledDialogContent>
                    <ScrollContainer onScroll={handleScroll}>
                        {loading && !results && <Loader />}
                        {results?.map((result) => (
                            <StyledCard
                                key={result.id}
                                elevation={0}
                                $selected={result.id === selectedNewsPostId}
                                onClick={() => setSelectedNewsPostId(result.id)}
                            >
                                <TinyPreviewCard
                                    iconName="NewsIcon"
                                    type="newsPost"
                                    tag={result.featuredCategory as NewsCategory}
                                    media={result.media as Medium[]}
                                    subtitle={result.title as string}
                                />
                            </StyledCard>
                        ))}
                    </ScrollContainer>
                </StyledDialogContent>
            ) : (
                <StyledDialogIcon>
                    <Icon size="5rem" name="Inactive" />
                    <Icon size="5rem" name="Ellipse" />
                    <Typography variant="body2">{t('favorites:noFavorites')}</Typography>
                </StyledDialogIcon>
            )}
            <StyledDialogActions>
                <Box display="flex" flexDirection="row" justifyContent="end">
                    <Button variant="outlined" onClick={handleClose}>
                        {t('common:cancel')}
                    </Button>
                    <Box ml={0.5}>
                        <Button
                            onClick={() => {
                                if (selectedNewsPostId) {
                                    onSelection(selectedNewsPostId);
                                }
                                handleClose();
                            }}
                            disabled={!selectedNewsPostId}
                        >
                            {t('news:link')}
                        </Button>
                    </Box>
                </Box>
            </StyledDialogActions>
        </StyledDialog>
    );
};

export default SelectNewsPostModal;
